import { useAuth0 } from '@auth0/auth0-react'
import * as Sentry from '@sentry/react'
import React, { useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Link, Navigate, useLocation } from 'react-router-dom'

/** Remove first-login keys written by PR #76 before server-side check-first-login. */
function clearLegacyFirstLoginStorage(userSub?: string | null): void {
  try {
    localStorage.removeItem('familyPortal.hasLoggedIn')
    sessionStorage.removeItem('familyPortal.freshLogin')
    if (userSub) {
      localStorage.removeItem(`familyPortal.hasLoggedIn.${userSub}`)
      sessionStorage.removeItem(`familyPortal.freshLogin.${userSub}`)
    }
  } catch {
    // ignore private browsing / quota errors
  }
}

const LoginCallback: React.FC = () => {
  const intl = useIntl()
  const { isAuthenticated, isLoading, error, user } = useAuth0()
  const location = useLocation()

  useEffect(() => {
    if (isAuthenticated) {
      clearLegacyFirstLoginStorage(user?.sub)
    }
  }, [isAuthenticated, user?.sub])

  useEffect(() => {
    if (error) {
      Sentry.captureException(error, {
        tags: {
          operation: 'auth.callback',
        },
        extra: {
          path: location.pathname,
          search: location.search,
        },
      })
    }
  }, [error, location.pathname, location.search])

  if (isAuthenticated) {
    return <Navigate to="/home" replace />
  }

  if (error) {
    const isMagicLinkError =
      /no email or no verification_code provided/i.test(error.message) ||
      /access_denied/i.test(String((error as { error?: string }).error))

    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 dark:bg-gray-900">
        <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
            <FormattedMessage id="loginCallback.error.heading" defaultMessage="Sign-in failed" />
          </h1>
          <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">{error.message}</p>
          {isMagicLinkError && (
            <>
              <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">
                <FormattedMessage
                  id="loginCallback.error.magicLinkHint"
                  defaultMessage="If you used the email link: open the link in the <strong>same browser</strong> (and tab) where you started sign-in. Otherwise try again and enter the code from the email."
                  values={{
                    strong: (chunks) => <strong>{chunks}</strong>,
                  }}
                />
              </p>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                <FormattedMessage
                  id="loginCallback.error.tenantHint"
                  defaultMessage='If you never saw Auth0&apos;s sign-in form, your tenant may need: Authentication Profile = "Identifier First", Passwordless Email connection enabled for this app, and Allowed Callback URLs including this origin.'
                />
              </p>
            </>
          )}
          <div className="mt-6">
            <Link
              to="/login"
              className="rounded-base inline-flex items-center justify-center bg-[#1f7a4d] px-4 py-2.5 text-sm font-medium text-white hover:bg-[#186239] focus:ring-2 focus:ring-[#1f7a4d] focus:ring-offset-2 focus:outline-none dark:focus:ring-offset-gray-800"
            >
              <FormattedMessage id="loginCallback.error.returnToLogin" defaultMessage="Return to login" />
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="rounded-lg bg-white p-6 shadow">
        <div className="text-sm text-gray-700">
          {isLoading
            ? intl.formatMessage({
                id: 'loginCallback.loading.completingSignIn',
                defaultMessage: 'Completing sign-in…',
              })
            : intl.formatMessage({ id: 'loginCallback.loading.continuing', defaultMessage: 'Continuing…' })}
        </div>
      </div>
    </div>
  )
}

export default LoginCallback
